import {Injectable, inject} from '@angular/core';
import {TYPE} from "../types/types";
import {HttpBackend, HttpClient, HttpEvent, HttpHeaders, HttpResponse} from "@angular/common/http";
import {finalize, map, Observable, shareReplay} from "rxjs";
import {environment} from "../../../environments/environment";
import {CalendarEventResource} from '../types/calendar-types/calendarEventResource';
import {CalendarEventUpdateRequest} from '../types/calendar-types/calendarEventUpdateRequest';
import {CalendarResource} from '../types/calendar-types/calendarResource';
import {CalendarPrioritySumResource} from '../types/calendar-types/calendarPrioritySumResource';
import {SystemAnnouncementResource} from '../types/announcements-types/systemAnnouncementResource';
import {KnowledgebaseSubPageResource} from '../types/knowledgebase-types/knowledgebaseSubPageResource';
import {KnowledgebasePageResource} from '../types/knowledgebase-types/knowledgebasePageResource';
import {KnowledgebaseContentResource} from '../types/knowledgebase-types/knowledgebaseContentResource';
import {KnowledgebasePageUpdateRequest} from '../types/knowledgebase-types/knowledgebasePageUpdateRequest';
import {KnowledgebasePageAdminResource} from '../types/knowledgebase-types/knowledgebasePageAdminResource';
import {KnowledgebaseContentUpdateRequest} from '../types/knowledgebase-types/knowledgebaseContentUpdateRequest';
import {KnowledgebaseMoveRequest} from '../types/knowledgebase-types/knowledgebaseMoveRequest';
import {UserMinimalResource} from '../types/knowledgebase-types/userMinimalResource';
import {KnowledgebasePageTreeResource} from '../types/knowledgebase-types/knowledgebasePageTreeResource';
import {CustomerAppearanceResource} from '../types/settings-types/customerAppearanceResource';
import {SettingsAppearanceRequest} from '../types/settings-types/settingsAppearanceRequest';
import {CustomerPublicResource} from '../types/settings-types/customerPublicResource';
import {CustomerResource} from '../types/settings-types/customerResource';
import {CustomerGdprResource} from '../types/settings-types/customerGdprResource';
import {SettingsGdprRequest} from '../types/settings-types/settingsGdprRequest';
import {CustomerTranslationProviderResource} from '../types/settings-types/customerTranslationProviderResource';
import {SettingsTranslationProviderRequest} from '../types/settings-types/settingsTranslationProviderRequest';
import {CustomerMenuResource} from '../types/settings-types/customerMenuResource';
import {CustomerMenuItemResource} from '../types/settings-types/customerMenuItemResource';
import {SettingsMenuCustomerRequest} from '../types/settings-types/settingsMenuCustomerRequest';
import {SettingsMenuCustomerTranslationUpdateRequest} from '../types/settings-types/settingsMenuCustomerTranslationUpdateRequest';
import {CustomerBrandingResource} from '../types/settings-types/customerBrandingResource';
import {UserMenuResource} from '../types/settings-types/userMenuResource';
import {UserMenuRequest} from '../types/settings-types/userMenuRequest';
import {ProfileResource} from '../types/user/profileResource';
import {FileStorageResource} from '../types/settings-types/fileStorageResource';
import {DashboardLinkResource} from '../types/dashboard-types/dashboardLinkResource';
import {SettingsDashboardLinkUpdateRequest} from '../types/settings-types/settingsDashboardLinkUpdateRequest';
import {CustomerProfileSettingsResource} from '../types/settings-types/customerProfileSettingsResource';
import {CustomerHomeResource} from '../types/settings-types/customerHomeResource';
import {UserProfileUpdateRequest} from '../types/user/userProfileUpdateRequest';
import {UserLoginResource} from '../types/user/userLoginResource';
import {UserProfileCustomInfoUpdateRequest} from '../types/user/userProfileCustomInfoUpdateRequest';
import {UserLoginRequest} from '../types/user/userLoginRequest';
import {UserPasswordResetRequest} from '../types/user/userPasswordResetRequest';
import {UserRegistrationRequest} from '../types/user/userRegistrationRequest';
import {UserFirebaseRequest} from '../types/user/userFirebaseRequest';
import {CapabilitiesResource} from '../types/user/capabilitiesResource';
import {UserDeviceType} from '../types/user/userDeviceType';
import {UserPasswordUpdateRequest} from '../types/user/userPasswordUpdateRequest';
import {UserAvatarUpdateRequest} from '../types/user/userAvatarUpdateRequest';
import {UserLanguageUpdateRequest} from '../types/user/userLanguageUpdateRequest';
import {UserAutoTranslationUpdateRequest} from '../types/user/userAutoTranslationUpdateRequest';
import {KnowledgebaseContentListResource} from '../types/knowledgebase-types/knowledgebaseContentListResource';
import {IAMUserState} from '../types/iam-types/iAMUserState';
import {IAMUserListResource} from '../types/iam-types/iAMUserListResource';
import {IAMGroupListResource} from '../types/iam-types/iAMGroupListResource';
import {IAMSectionResource} from '../types/iam-types/iAMSectionResource';
import {IAMRightsUpdateRequest} from '../types/iam-types/iAMRightsUpdateRequest';
import {IAMUserInfoResource} from '../types/iam-types/iAMUserInfoResource';
import {IAMGroupInfoResource} from '../types/iam-types/iAMGroupInfoResource';
import {IAMUserResource} from '../types/iam-types/iAMUserResource';
import {IAMGroupResource} from '../types/iam-types/iAMGroupResource';
import {IAMUserGroupResource} from '../types/iam-types/iAMUserGroupResource';
import {ChatListFilterType} from '../types/chat-types/chatListFilterType';
import {ChatListResource} from '../types/chat-types/chatListResource';
import {ChatUpdateRequest} from '../types/chat-types/chatUpdateRequest';
import {ChatResource} from '../types/chat-types/chatResource';
import {ChatMessageResource} from '../types/chat-types/chatMessageResource';
import {ChatMessageUpdateRequest} from '../types/chat-types/chatMessageUpdateRequest';
import {ChatMessageCursorType} from '../types/chat-types/chatMessageCursorType';
import {ChatMessageReaderResource} from '../types/chat-types/chatMessageReaderResource';
import {DashboardFavoriteResource} from '../types/dashboard-types/dashboardFavoriteResource';
import {DashboardTileResource} from '../types/dashboard-types/dashboardTileResource';
import {UserContactResource} from '../types/contact-types/userContactResource';
import {ChatShareCounts} from '../types/chat-types/chatShareCounts';
import {LinkResource} from '../types/chat-types/linkResource';
import {EnvironmentService} from './environment.service';
import {UserContactListResource} from '../types/contact-types/userContactListResource';
import {NotificationResource} from '../types/notifications-types/notificationResource';
import {NotificationReadState} from '../types/notifications-types/notificationReadState';
import {NotificationChannel} from '../types/notifications-types/notificationChannel';
import {NewsResource} from '../types/news-types/newsResource';
import {NewsUserReadResource} from '../types/news-types/newsUserReadResource';
import {NewsUserLikeResource} from '../types/news-types/newsUserLikeResource';
import {NewsCreateRequest} from '../types/news-types/newsCreateRequest';
import {NewsUpdateRequest} from '../types/news-types/newsUpdateRequest';
import {NewsPinUpdateRequest} from '../types/news-types/newsPinUpdateRequest';
import {NewsEmojiUpdateRequest} from '../types/news-types/newsEmojiUpdateRequest';
import {NewsCommentResource} from '../types/news-types/newsCommentResource';
import {NewsCommentCreateRequest} from '../types/news-types/newsCommentCreateRequest';
import {IAMUserActivateRequest} from '../types/iam-types/iAMUserActivateRequest';
import {IAMUserUpdateRequest} from '../types/iam-types/iAMUserUpdateRequest';
import {IAMGroupUpdateRequest} from '../types/iam-types/iAMGroupUpdateRequest';
import {ShareEmailRequest} from '../types/share-email-types/shareEmailRequest';
import {GroupMinimalResource} from '../types/user/groupMinimalResource';
import {CustomerActionType} from '../types/user/customerActionType';
import {StorageModule} from '../types/settings-types/storageModule';
import {StorageFilesystemGet200Response} from '../types/settings-types/storageFilesystemGet200Response';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http: HttpClient = inject(HttpClient);
  // HttpClient instance that bypasses interceptors
  private rawHttp: HttpClient = new HttpClient(inject(HttpBackend));
  private environmentService: EnvironmentService = inject(EnvironmentService);

  private url: string = environment.api.url;
  private headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json', 'X-Socket-ID': this.environmentService.getTabId() || ''});

  public setUrl(url: string): void {
    this.url = url;
  }

  public getUrl(): string {
    return this.url;
  }

  public setLanguageByKey(key: string, value: string): Observable<boolean> {
    return this.http.post<boolean>(this.url + '/' + environment.api.version + '/languages/value',
      {
        key,
        value,
        language: 'de'
      },
      {
        headers: this.headers
      });
  }

  //#region feedback
  public getFeedback(jsuID: number): Observable<TYPE.FeedbackListItem> {
    return this.http.post<TYPE.FeedbackListItem>(
      this.url + '/' + environment.api.version + '/survey/show',
      {
        jsuid: jsuID
      },
      {
        headers: this.headers
      });
  }

  public getFeedbacks(page: number, role: 'admin' | 'user', tab: 'active' | 'draft' | 'archive', searchString: string, filter: string): Observable<TYPE.FeedbackListItem[]> {
    return this.http.post<TYPE.FeedbackListItem[]>(
      this.url + '/' + environment.api.version + '/survey/list',
      {
        page,
        searchString: (searchString ? this.preFilter(searchString) : ''),
        view: role,
        filter: tab, // TODO should be status
        type: filter // TODO should be filter
      },
      {
        headers: this.headers
      });
  }

  public getFeedbackQuotas(page: number, feedbackID: number, filter: string): Observable<TYPE.FeedbackListItem[]> {
    return this.http.post<TYPE.FeedbackListItem[]>(
      this.url + '/' + environment.api.version + '/survey/quota',
      {
        jsid: feedbackID,
        page,
        filter
      },
      {
        headers: this.headers
      });
  }

  public setFeedback(feedback: TYPE.FeedbackListItem): Observable<TYPE.FeedbackListItem> {
    return this.http.post<TYPE.FeedbackListItem>(this.url + '/' + environment.api.version + '/survey/' + (feedback.id ? 'edit' : 'create'), feedback, {headers: this.headers});
  }

  public advanceFeedback(feedback: TYPE.FeedbackListItem, advance: boolean, jsuID: number): Observable<TYPE.FeedbackListItem> {
    return this.http.post<TYPE.FeedbackListItem>(this.url + '/' + environment.api.version + '/survey/advance',
      {
        feedback,
        advance,
        jsuid: jsuID
      },
      {
        headers: this.headers
      });
  }

  public instancesFeedback(feedbackID: number): Observable<TYPE.FieldSettingsModelWithId[]> {
    return this.http.post<TYPE.FieldSettingsModelWithId[]>(this.url + '/' + environment.api.version + '/survey/instances', {
      jsid: feedbackID
    },
      {
        headers: this.headers
      });
  }

  public statisticsFeedback(feedbackID: number, instanceIDs: number[], userIDs: number[], groupIDs: number[]): Observable<TYPE.FeedbackStatsItem> {
    return this.http.post<TYPE.FeedbackStatsItem>(this.url + '/' + environment.api.version + '/survey/statistics', {
      jsid: feedbackID,
      instanceIDs,
      userIDs,
      groupIDs
    },
      {
        headers: this.headers
      });
  }

  public passedFailedFeedback(feedbackID: number, type: 'passed' | 'failed', page: number, searchString: string): Observable<TYPE.FeedbackPassedFaileds[]> {
    return this.http.post<TYPE.FeedbackPassedFaileds[]>(this.url + '/' + environment.api.version + '/survey/' + type + '/' + feedbackID + '/' + page, {
      searchString: (searchString ? this.preFilter(searchString) : '')
    },
      {
        headers: this.headers
      });
  }

  public guestLinkFeedback(customerKey: string, jssID: number): Observable<TYPE.FeedbackUser> {
    return this.http.get<TYPE.FeedbackUser>(this.url + '/' + environment.api.version + '/survey/link/' + customerKey + '/' + jssID, {headers: this.headers});
  }
  //#endregion feedback
  //#region workflow
  public getWorkflow(jsuID: number): Observable<TYPE.WorkflowListItem> {
    return this.http.post<TYPE.WorkflowListItem>(
      this.url + '/' + environment.api.version + '/workflow/show',
      {
        jsuid: jsuID
      },
      {
        headers: this.headers
      });
  }

  public getWorkflows(page: number, role: 'admin' | 'super' | 'user', tab: 'active' | 'progress' | 'draft' | 'cancelled' | 'todo' | 'completed', searchString: string, filter: string): Observable<TYPE.WorkflowListItem[]> {
    return this.http.post<TYPE.WorkflowListItem[]>(
      this.url + '/' + environment.api.version + '/workflow/list',
      {
        page,
        searchString: (searchString ? this.preFilter(searchString) : ''),
        role: role === 'user' ? 'participant' : role,
        filter: tab, // TODO should be status
        type: filter // TODO should be filter
      },
      {
        headers: this.headers
      });
  }

  public getWorkflowQuotas(page: number, workflowID: number, filter: string): Observable<TYPE.WorkflowListItem[]> {
    return this.http.post<TYPE.WorkflowListItem[]>(
      this.url + '/' + environment.api.version + '/workflow/quota',
      {
        jsid: workflowID,
        page,
        filter
      },
      {
        headers: this.headers
      });
  }

  public setWorkflow(workflow: TYPE.WorkflowListItem): Observable<TYPE.WorkflowListItem> {
    return this.http.post<TYPE.WorkflowListItem>(this.url + '/' + environment.api.version + '/workflow/' + (workflow.id ? 'edit' : 'create'), workflow, {headers: this.headers});
  }
  //#endregion workflow

  //#region communities
  public getSubscribedTopicCommunities(page: number, parentID: number, searchString: string): Observable<TYPE.CommunityListItem> {
    return this.http.post<TYPE.CommunityListItem>(
      this.url + '/' + environment.api.version + '/forum/getList/' + page,
      {
        searchString: (searchString ? this.preFilter(searchString) : ''),
        is_favorites: 1,
        page: page,
        parent_id: parentID
      },
      {
        headers: this.headers
      });
  }

  public getTrendingTopicCommunities(page: number, searchString: string): Observable<TYPE.CommunityListItem> {
    return this.http.post<TYPE.CommunityListItem>(
      this.url + '/' + environment.api.version + '/forum/getList/' + page,
      {
        searchstring: (searchString ? this.preFilter(searchString) : ''),
        is_trending: 1,
        page: page
      },
      {
        headers: this.headers
      });
  }

  public getCommunities(page: number, parentID: number, searchString: string): Observable<TYPE.CommunityListItem> {
    return this.http.post<TYPE.CommunityListItem>(
      this.url + '/' + environment.api.version + '/forum/getList/' + parentID,
      {
        searchstring: (searchString ? this.preFilter(searchString) : ''),
        page: page,
        parent_id: parentID
      },
      {
        headers: this.headers
      });
  }

  public getDetailCommunities(page: number, channelID: number, entryID: number, searchString: string): Observable<TYPE.CommunityDetail> {
    return this.http.post<TYPE.CommunityDetail>(
      this.url + '/' + environment.api.version + '/forum/getDetail',
      {
        searchstring: (searchString ? this.preFilter(searchString) : ''),
        page: page,
        channel_id: channelID,
        entry_id: entryID
      },
      {
        headers: this.headers
      });
  }

  public setCommunity(communityListItem: TYPE.CommunityListItem, userIDs: string, groupIDs: string): Observable<TYPE.CommunityListItem> {
    return this.http.post<TYPE.CommunityListItem>(
      this.url + '/' + environment.api.version + '/forum/setChannel',
      {
        id: communityListItem.id || null,
        parent_id: communityListItem.parent_id || null,
        title: communityListItem.title,
        media: communityListItem.media,
        type: 'folder',
        users: userIDs,
        groups: groupIDs,
        is_private: communityListItem.is_private,
        is_listed: communityListItem.is_listed,
      },
      {
        headers: this.headers
      });
  }

  public setTopicDiscussionCommunity(communityDetailEntry: TYPE.CommunityDetailEntry): Observable<TYPE.CommunityDetailEntry> {
    return this.http.post<TYPE.CommunityDetailEntry>(
      this.url + '/' + environment.api.version + '/forum/setChannelEntry',
      {
        id: communityDetailEntry.id || null,
        channel_id: communityDetailEntry.channel_id,
        parent_id: communityDetailEntry.parent_id || null,
        title: communityDetailEntry.title,
        message: communityDetailEntry.message_web,
        media: communityDetailEntry.media,
        files: communityDetailEntry.files,
      },
      {
        headers: this.headers
      });
  }

  public getTreeCommunity(page: number, searchString: string): Observable<TYPE.CommunityListItem[]> {
    return this.http.post<TYPE.CommunityListItem[]>(
      this.url + '/' + environment.api.version + '/forum/pageTree/' + page + (searchString ? '/' + this.preFilter(searchString) : ''),
      {
        searchString: (searchString ? this.preFilter(searchString) : ''),
        page: page
      },
      {
        headers: this.headers
      });
  }

  public deleteCommunity(communityID: number): Observable<boolean> {
    return this.http.delete<boolean>(
      this.url + '/' + environment.api.version + '/forum/delete/' + communityID,
      {
        headers: this.headers
      });
  }

  public deleteTopicCommunity(topicID: number): Observable<boolean> {
    return this.http.delete<boolean>(
      this.url + '/' + environment.api.version + '/forum/delete/' + topicID,
      {
        headers: this.headers
      });
  }

  public deleteTopicDiscussionCommunity(entryID: number): Observable<boolean> {
    return this.http.delete<boolean>(
      this.url + '/' + environment.api.version + '/forum/deleteEntry/' + entryID,
      {
        headers: this.headers
      });
  }

  public likeTopicDiscussionCommunity(entryID: number): Observable<TYPE.CommunityDetailEntry> {
    return this.http.post<TYPE.CommunityDetailEntry>(
      this.url + '/' + environment.api.version + '/forum/setChannelEntryLike',
      {
        entry_id: entryID
      },
      {
        headers: this.headers
      });
  }

  public getTopicDiscussionCommentCommunity(page: number, entryID: number): Observable<TYPE.CommunityDetailEntry[]> {
    return this.http.get<TYPE.CommunityDetailEntry[]>(this.url + '/' + environment.api.version + '/forum/comments/' + entryID + '/' + page, {headers: this.headers});
  }

  public setTopicDiscussionCommentCommunity(communityDetailEntry: TYPE.CommunityDetailEntry): Observable<TYPE.CommunityDetailEntry> {
    return this.http.post<TYPE.CommunityDetailEntry>(
      this.url + '/' + environment.api.version + '/forum/setChannelEntry',
      {
        id: communityDetailEntry.id || null,
        channel_id: communityDetailEntry.channel_id,
        parent_id: communityDetailEntry.parent_id || null,
        title: communityDetailEntry.title,
        message: communityDetailEntry.message_web,
        media: communityDetailEntry.media
      },
      {
        headers: this.headers
      });
  }

  public subscribeTopicCommunity(topicID: number, abo: boolean): Observable<boolean> {
    return this.http.post<boolean>(
      this.url + '/' + environment.api.version + '/forum/aboBell',
      {
        channel_id: topicID,
        abo
      },
      {
        headers: this.headers
      });
  }

  public applyTopicCommunity(topicID: number, applied: boolean): Observable<boolean> {
    return this.http.post<boolean>(
      this.url + '/' + environment.api.version + '/forum/application',
      {
        channel_id: topicID,
        subscribe: applied
      },
      {
        headers: this.headers
      });
  }

  public notificationTopicCommunity(topicID: number, type: string, mail: boolean, push: boolean): Observable<TYPE.CommunityListItem> {
    return this.http.post<TYPE.CommunityListItem>(
      this.url + '/' + environment.api.version + '/forum/aboBell',
      {
        channel_id: topicID,
        notification_type: type,
        is_mail: mail,
        is_push: push
      },
      {
        headers: this.headers
      });
  }

  public setTopicCommunity(communityListItem: TYPE.CommunityListItem, userIDs: string, groupIDs: string): Observable<TYPE.CommunityListItem> {
    return this.http.post<TYPE.CommunityListItem>(
      this.url + '/' + environment.api.version + '/forum/setChannel',
      {
        id: communityListItem.id || null,
        parent_id: communityListItem.parent_id || null,
        title: communityListItem.title,
        description: communityListItem.description,
        media: communityListItem.media,
        type: 'channel',
        users: userIDs,
        groups: groupIDs,
        is_private: communityListItem.is_private,
        is_listed: communityListItem.is_listed,
      },
      {
        headers: this.headers
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public searchCommunities(page: number, parentID: number, searchString: string, filter: string): Observable<any> {
    return this.http.post(
      this.url + '/' + environment.api.version + '/forum/search/' + page,
      {
        searchstring: (searchString ? this.preFilter(searchString) : ''),
        parent_id: parentID,
        filter
      },
      {
        headers: this.headers
      });
  }
  //#endregion communities
  // elements
  public getGroups(
    page: number,
    searchString: string,
    _showCounter: boolean,
    type: 'own' | 'all' | 'active' | 'inactive' | 'registration' | CustomerActionType | '' = '',
    can: CustomerActionType | '' = '',
    limit: number = 25
  ): Observable<GroupMinimalResource[]> {
    const normalizedSearch = this.preFilter(searchString);
    const mappedAction = can || this.mapLegacyTypeToCustomerAction(type);
    const params: Record<string, string> = {
      skip: String(page * limit),
      limit: String(limit)
    };

    if (normalizedSearch !== false && normalizedSearch !== '') {
      params['search'] = normalizedSearch;
    }
    if (mappedAction !== undefined) {
      params['can'] = mappedAction;
    }

    return this.http.get<GroupMinimalResource[]>(
      this.url + '/' + environment.api.version + '/customer/groups',
      {headers: this.headers, params}
    );
  }

  // helper
  private mapLegacyTypeToCustomerAction(type: 'own' | 'all' | 'active' | 'inactive' | 'registration' | CustomerActionType | '' | string): CustomerActionType | undefined {
    switch (type) {
      case CustomerActionType.Calendar:
      case CustomerActionType.Chat:
      case CustomerActionType.Knowledgebase:
      case CustomerActionType.News:
        return type;
      default:
        return undefined;
    }
  }

  private preFilter(searchString: string): string | false {
    if (searchString === undefined) {
      searchString = '';
    }
    const regex = /\//g;
    const isRegex = regex.test(searchString);
    if (isRegex) {
      searchString = searchString.replace(/\//g, '');
      if (searchString === '') {
        return false;
      }
    }
    encodeURIComponent(searchString);
    return searchString;
  }

  public storageUpload(file: File, directory?: string, isPublic?: boolean): Observable<HttpEvent<FileStorageResource>>  {
    const formData: FormData = new FormData();
    formData.append('file', file);
    if (directory) {
      formData.append('directory', directory);
    }
    if (isPublic) {
      formData.append('public', isPublic.toString())
    }

    return this.http.post<FileStorageResource>(this.url + '/' + environment.api.version + '/storage/upload', formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  public downloadByPath(path: string): Observable<Blob> {
    return this.http.get(this.url + path, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  public downloadByUrl(url: string): Observable<Blob> {
    return this.rawHttp.get(url, {
      responseType: 'blob'
    });
  }


  //#region Calendar
  public getCalendars(): Observable<CalendarResource[]> {
    return this.http.get<CalendarResource[]>(this.url + '/' + environment.api.version + '/calendars', {headers: this.headers});
  }

  public getCalendarEvents(calendarId: number, startDate: string, endDate: string): Observable<CalendarEventResource[]> {
    return this.http.get<CalendarEventResource[]>(`${this.url}/${environment.api.version}/calendar/${calendarId}?start=${startDate}&end=${endDate}`, {headers: this.headers});
  }
    
  public createOrUpdateCalendarEvent(event: CalendarEventUpdateRequest, eventId: number | null, etag: string, overrideDetails: boolean, overrideGuests: boolean, overrideTiming: boolean): Observable<CalendarEventResource[]> {
    const headers = this.headers;
    if(etag.trim() !== '') {
      headers.append('If-Match', etag);
    }
    return this.http.put<CalendarEventResource[]>(
      `${this.url}/${environment.api.version}/calendar/event${eventId ? '/' + eventId : ''}?adaptOverrideDetails=${overrideDetails}&adaptOverrideGuests=${overrideGuests}&adaptOverrideTiming=${overrideTiming}`,
      event,
      {headers: headers}
    );
  }

  public deleteCalendarEvent(eventID: number): Observable<CalendarEventResource> {
    return this.http.delete<CalendarEventResource>(`${this.url}/${environment.api.version}/calendar/event/${eventID}`, {headers: this.headers})
  }

  public getCalendarEventsForToday(calendarId: number): Observable<CalendarEventResource[]> {
    return this.http.get<CalendarEventResource[]>(`${this.url}/${environment.api.version}/calendar/${calendarId}/today?timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`, {headers: this.headers});
  }

  public getCalendarDatePriorities(calendarId: number, start: string, end: string): Observable<Record<string, CalendarPrioritySumResource>> {
    return this.http.get<Record<string, CalendarPrioritySumResource>>(`${this.url}/${environment.api.version}/calendar/${calendarId}/priorities?start=${start}&end=${end}&timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`, {headers: this.headers});
  }

  public getCalendarEventParticipants(eventId: number, options?: {search?: string; skip?: number; limit?: number}): Observable<UserMinimalResource[]> {
    let query = '';
    if (options?.search) {
      query += `${query ? '&' : '?'}search=${encodeURIComponent(options.search)}`;
    }
    if (options?.skip !== undefined) {
      query += `${query ? '&' : '?'}skip=${options.skip}`;
    }
    if (options?.limit !== undefined) {
      query += `${query ? '&' : '?'}limit=${options.limit}`;
    }

    return this.http.get<UserMinimalResource[]>(
      `${this.url}/${environment.api.version}/calendar/event/${eventId}/participants${query}`,
      {headers: this.headers}
    );
  }

  //#endregion Calendar

  //#region Knowledgebase
  //#region new Knowledgebase APIs
    //#region Pages
    public getKnowledgebaseRootPages(sort: TYPE.KnowledgebaseCategoryOrder, search: string, limit: number, after: number | null): Observable<{ data: KnowledgebaseSubPageResource[], moreEntries: boolean }> {
      const searchParam = search ? `&search=${search}` : '';
      const afterParam = after !== null ? `&after=${after}` : '';
        return this.http.get<KnowledgebaseSubPageResource[]>(
          this.url + '/' + environment.api.version + '/knowledgebase/page/root/subpages'
          + `?sort=${sort}${searchParam}&limit=${limit}${afterParam}`,
          {observe: 'response'}
        ).pipe(
          map((response: HttpResponse<KnowledgebaseSubPageResource[]>) => ({
            data: response.body || [],
            moreEntries: response.headers.get('x-more-entries') === 'true'
          }))
        );
    }

    public getKnowledgebasePages(pageId: number, sort: TYPE.KnowledgebaseCategoryOrder, search: string, limit: number, after: number | null): Observable<{ data: KnowledgebasePageResource, moreEntries: boolean }> {
      const searchParam = search ? `&search=${search}` : '';
      const afterParam = after !== null ? `&after=${after}` : '';
      return this.http.get<KnowledgebasePageResource>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId + '/subpages'
        + `?sort=${sort}${searchParam}&limit=${limit}${afterParam}`,
        {observe: 'response'}
      ).pipe(
        map((response: HttpResponse<KnowledgebasePageResource>) => ({
          data: response.body!,
          moreEntries: response.headers.get('x-more-entries') === 'true'
        }))
      );
    }

    public getKnowledgebasePageById(pageId: number): Observable<KnowledgebasePageAdminResource>{
      return this.http.get<KnowledgebasePageAdminResource>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId,
      );
    }

    public createOrUpdateKnowledgebasePage(data: KnowledgebasePageUpdateRequest, existingID: number | null): Observable<KnowledgebasePageAdminResource> {
      return this.http.put<KnowledgebasePageAdminResource>(
        this.url + '/' + environment.api.version + '/knowledgebase/page' + (existingID ? '/' + existingID : ''),
        data,
        {headers: this.headers}
      );
    }
    
    public deleteKnowledgebasePage(pageId: number): Observable<boolean> {
      return this.http.delete<boolean>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId,
        {headers: this.headers}
      );
    }

    public setKnowledgebasePageFavorite(pageId: number, favorite: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId + '/favorite',
        {favorite: favorite},
        {headers: this.headers},
      );
    }

    public updateKnowledgebasePageOrder(pageId: number, data: KnowledgebaseMoveRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId + '/move',
        data,
        {headers: this.headers},
      );
    }

    public getKnowledgebasePageReaders(pageId: number, query: string, limit: number, skip: number): Observable<UserMinimalResource[]> {
      const searchParam = query ? `&search=${query}` : '';
      return this.http.get<UserMinimalResource[]>(
        this.url + '/' + environment.api.version + '/knowledgebase/content/' + pageId + '/readers'
        + `?limit=${limit}&skip=${skip}${searchParam}`,
        {headers: this.headers},
      );
    }

    public getKnowledgebasePageTree(query: string): Observable<KnowledgebasePageTreeResource[]> {
      return this.http.get<KnowledgebasePageTreeResource[]>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/tree' + (query ? '?search=' + query : ''),
        {headers: this.headers},
      );
    }
  //#endregion Pages

    //#region Content
    public getKnowledgebasePageContent(pageId: number, sort: TYPE.KnowledgebaseCategoryContentOrder, search: string, limit: number, skip: number): Observable<KnowledgebaseContentListResource[]>{
      const searchParam = search ? `&search=${search}` : '';
      return this.http.get<KnowledgebaseContentListResource[]>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId + '/content'
        + `?sort=${sort}${searchParam}&limit=${limit}&skip=${skip}&searchMode=articles`,
      );
    }

    public getKnowledgebasePageContentFiles(pageId: number, sort: TYPE.KnowledgebaseCategoryContentOrder, search: string, limit: number, skip: number): Observable<FileStorageResource[]>{
      const searchParam = search ? `&search=${search}` : '';
      return this.http.get<FileStorageResource[]>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId + '/files'
        + `?sort=${sort}${searchParam}&limit=${limit}&skip=${skip}&searchMode=files`,
        {headers: this.headers},
      );
    }

    public getKnowledgebasePageContentById(pageId: number): Observable<KnowledgebaseContentResource>{
      return this.http.get<KnowledgebaseContentResource>(
        this.url + '/' + environment.api.version + '/knowledgebase/content/' + pageId,
      );
    }

    public deleteKnowledgebasePageContent(articleID: number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + '/knowledgebase/content/' + articleID,
        {headers: this.headers}
      );
    }

    public createOrUpdateKnowledgebasePageContent(data: KnowledgebaseContentUpdateRequest, existingID: number | null): Observable<KnowledgebaseContentResource> {
      return this.http.put<KnowledgebaseContentResource>(
        this.url + '/' + environment.api.version + '/knowledgebase/content' + (existingID ? '/' + existingID : ''),
        data,
        {headers: this.headers}
      );
    }

    public updateKnowledgebasePageContentOrder(articleID: number, data: KnowledgebaseMoveRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/knowledgebase/content/' + articleID + '/move',
        data,
        {headers: this.headers},
      );
    }

    public setKnowledgebasePageContentFavorite(articleID: number, favorite: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/knowledgebase/content/' + articleID + '/favorite',
        {favorite: favorite},
        {headers: this.headers},
      );
    }

    //#endregion Content

    public importKnowledgebasePage(categoryId: number, id: number): Observable<void> {
      return this.http.post<void>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + categoryId + '/import',
        {file_storage_id: id},
        {headers: this.headers}
      );
    }

    public exportKnowledgebasePage(pageId: number): Observable<string | ArrayBuffer> {
      return this.http.get<string | ArrayBuffer>(
        this.url + '/' + environment.api.version + '/knowledgebase/page/' + pageId + '/export',
        {
          headers: this.headers,
          responseType: 'arraybuffer' as 'json'
        }
      );
    }
  //#endregion new Knowledgebase APIs
  //#endregion Knowledgebase

  //#region share
  public shareByMail(data: ShareEmailRequest): Observable<void> {
    return this.http.post<void>(
      this.url + '/' + environment.api.version + '/share/email',
      data,
      {headers: this.headers}
      );
  }
  //#endregion share

  //#region System Announcement
    public getMaintenanceInfo(): Observable<SystemAnnouncementResource[]> {
      return this.http.get<SystemAnnouncementResource[]>(this.url + '/' + environment.api.version + '/announcement/upcoming', {headers: this.headers});
    }
  //#endregion System Announcement

  //#region theme
    public getAppearance(): Observable<CustomerAppearanceResource> {
      return this.http.get<CustomerAppearanceResource>(this.url + '/' + environment.api.version + '/settings/appearance', {headers: this.headers});
    }

    public setAppearance(data: SettingsAppearanceRequest): Observable<CustomerAppearanceResource> {
      return this.http.put<CustomerAppearanceResource>(
        this.url + '/' + environment.api.version + '/settings/appearance',
        data,
        {headers: this.headers});
    }
  //#endregion theme

  //#region customer
    public getPublicCustomerResource(customerKey: string): Observable<CustomerPublicResource> {
      return this.http.get<CustomerPublicResource>(
        this.url + '/' + environment.api.version + `/customer/${customerKey}/public`,
        {headers: this.headers});
    }
    
    public getCustomerResource(): Observable<CustomerResource> {
      return this.http.get<CustomerResource>(
        this.url + '/' + environment.api.version + `/customer`,
        {headers: this.headers});
    }

    public getUsers(
      page: number,
      searchString: string,
      type: 'own' | 'all' | 'active' | 'inactive' | 'registration' | CustomerActionType | '' = '',
      can: CustomerActionType | '' = '',
      limit: number = 25
    ): Observable<UserMinimalResource[]> {
      const normalizedSearch = this.preFilter(searchString);
      const mappedAction = can || this.mapLegacyTypeToCustomerAction(type);
      const params: Record<string, string> = {
        skip: String(page * limit),
        limit: String(limit)
      };

      if (normalizedSearch !== false && normalizedSearch !== '') {
        params['search'] = normalizedSearch;
      }
      if (mappedAction !== undefined) {
        params['can'] = mappedAction;
      }

      return this.http.get<UserMinimalResource[]>(
        this.url + '/' + environment.api.version + '/customer/users',
        {headers: this.headers, params}
      );
    }

  //#endregion customer

  //#region gdpr
    public getSettingsGdpr(language: string): Observable<HttpResponse<CustomerGdprResource>> {
      return this.http.get<CustomerGdprResource>(
        this.url + '/' + environment.api.version + `/settings/gdpr/${language}`,
        {headers: this.headers, observe: 'response'});
    }

    public setSettingsGdpr(data: SettingsGdprRequest, language: string): Observable<CustomerGdprResource> {
      return this.http.put<CustomerGdprResource>(
        this.url + '/' + environment.api.version + `/settings/gdpr/${language}`,
        data,
        {headers: this.headers});
    }

  //#endregion gdpr

  //#region translation providers
    public getSettingsTranslationProviders(): Observable<CustomerTranslationProviderResource> {
      return this.http.get<CustomerTranslationProviderResource>(
        this.url + '/' + environment.api.version + `/settings/translation/provider`,
        {headers: this.headers});
    }

    public setSettingsTranslationProviders(data: SettingsTranslationProviderRequest): Observable<CustomerTranslationProviderResource> {
      return this.http.put<CustomerTranslationProviderResource>(
        this.url + '/' + environment.api.version + `/settings/translation/provider`,
        data,
        {headers: this.headers});
    }
  //#endregion translation providers

  //#region customer menu
    public getSettingsCustomerMenu(language: string): Observable<CustomerMenuResource> {
      return this.http.get<CustomerMenuResource>(
        this.url + '/' + environment.api.version + `/settings/menu/customer/${language}`,
        {headers: this.headers});
    }

    public setSettingsCustomerMenuTranslations(data: SettingsMenuCustomerTranslationUpdateRequest, language: string): Observable<CustomerMenuItemResource[]> {
      return this.http.put<CustomerMenuItemResource[]>(
        this.url + '/' + environment.api.version + `/settings/menu/customer/${language}`,
        data,
        {headers: this.headers});
    }

    public setSettingsCustomerMenuOptions(data: SettingsMenuCustomerRequest): Observable<CustomerMenuItemResource[]> {
      return this.http.put<CustomerMenuItemResource[]>(
        this.url + '/' + environment.api.version + `/settings/menu/customer`,
        data,
        {headers: this.headers});
    }
  //#endregion customer menu

  //#region user menu
    public getSettingsUserMenu(): Observable<UserMenuResource> {
      return this.http.get<UserMenuResource>(
        this.url + '/' + environment.api.version + `/settings/menu/user`,
        {headers: this.headers});
    }

    public setSettingsUserMenu(data: UserMenuRequest): Observable<UserMenuResource> {
      return this.http.put<UserMenuResource>(
        this.url + '/' + environment.api.version + `/settings/menu/user`,
        data,
        {headers: this.headers});
    }
  //#endregion user menu

  //#region branding
    public getSettingsBranding(): Observable<CustomerBrandingResource> {
      return this.http.get<CustomerBrandingResource>(
        this.url + '/' + environment.api.version + `/settings/branding`,
        {headers: this.headers});
    }

    public setSettingsBranding(id: string): Observable<CustomerBrandingResource> {
      return this.http.put<CustomerBrandingResource>(
        this.url + '/' + environment.api.version + `/settings/branding`,
        {file_storage_id: id},
        {headers: this.headers}
      );
    }
  //#endregion branding

  //#region user
    private refreshTokenRequest$: Observable<{ token: string }> | null = null;

    public getUserFromForm(data: UserLoginRequest): Observable<HttpResponse<UserLoginResource>> {
      return this.http.post<UserLoginResource>(
        this.url + '/' + environment.api.version + '/user/login',
        data,
        {
          headers: this.headers,
          observe: 'response'
        }
      );
    }

    public logoutUser(): Observable<void>{
      return this.http.post<void>(
        this.url + '/' + environment.api.version + '/user/logout',
        {},
        {headers: this.headers}
      );
    }

    public resetUserPassword(data: UserPasswordResetRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/user/password/reset',
        data,
        {
          headers: this.headers
        });
    }

    public setUserRegister(data: UserRegistrationRequest): Observable<void> {
      return this.http.post<void>(
        this.url + '/' + environment.api.version + '/user/registration',
        data,
        {
          headers: this.headers
        });
    }

    public setUserFirebase(data: UserFirebaseRequest): Observable<void> {
      return this.http.post<void>(
        this.url + '/' + environment.api.version + '/user/firebase',
        data,
        {
          headers: this.headers
        });
    }

    public getUserCapabilities(): Observable<CapabilitiesResource> {
      return this.http.get<CapabilitiesResource>(
        this.url + '/' + environment.api.version + '/user/capabilities',
        {headers: this.headers}
      );
    }

    public getUserMenu(deviceType: UserDeviceType = 'web'): Observable<UserMenuResource> {
      return this.http.get<UserMenuResource>(
        this.url + '/' + environment.api.version + `/user/menu/${deviceType}`,
        {headers: this.headers});
    }

    public getUserProfile(): Observable<ProfileResource> {
      return this.http.get<ProfileResource>(
        this.url + '/' + environment.api.version + `/user/profile`,
        {headers: this.headers});
    }

    public getUserGdpr(): Observable<HttpResponse<CustomerGdprResource>> {
      return this.http.get<CustomerGdprResource>(
        this.url + '/' + environment.api.version + `/user/gdpr`,
        {headers: this.headers, observe: 'response'}
      );
    }

    public acceptUserGdpr():Observable<void> {
      return this.http.post<void>(
        this.url + '/' + environment.api.version + `/user/gdpr/accept`,
        {},
        {headers: this.headers}
      );
    }

    public setUserProfileLanguage(language: string): Observable<void> {
      const data: UserLanguageUpdateRequest = {language};
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/user/language`,
        data,
        {headers: this.headers}
      );
    }

    public setUserProfileAutoTranslation(enabled: boolean): Observable<void> {
      const data: UserAutoTranslationUpdateRequest = {auto_translation: enabled};
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/user/auto-translation`,
        data,
        {headers: this.headers}
      );
    }

    public setUserProfileAvatar(fileStorageId: number | null): Observable<void> {
      const data: UserAvatarUpdateRequest = {file_storage_id: fileStorageId};
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/user/avatar`,
        data,
        {headers: this.headers}
      );
    }

    public updateUserProfile(data: UserProfileUpdateRequest): Observable<ProfileResource> {
      return this.http.put<ProfileResource>(
        this.url + '/' + environment.api.version + `/user/profile`,
        data,
        {headers: this.headers}
      );
    }

    public updateUserProfileCustomFields(data: UserProfileCustomInfoUpdateRequest): Observable<ProfileResource> {
      return this.http.put<ProfileResource>(
        this.url + '/' + environment.api.version + `/user/profile/custom`,
        data,
        {headers: this.headers}
      );
    }

    public updateUserProfilePassword(password: string): Observable<UserLoginResource> {
      const data: UserPasswordUpdateRequest = {password};
      return this.http.put<UserLoginResource>(
        this.url + '/' + environment.api.version + `/user/password`,
        data,
        {headers: this.headers}
      );
    }

    public translate(message: string): Observable<{ text: string }> {
      const body: {text: string} = {
        text: message,
      };
      return this.http.post<{ text: string }>(this.url + '/' + environment.api.version + '/translate', body, {
        headers: this.headers
      });
    }

    public refreshToken(token: string): Observable<{ token: string }> {
      if (!this.refreshTokenRequest$) {
        this.refreshTokenRequest$ = this.rawHttp.post<{ token: string }>(
          this.url + '/' + environment.api.version + '/user/refresh',
          {},
          {
            headers: new HttpHeaders({'Authorization': `Bearer ${token}`})
          }
        ).pipe(
          finalize(() => {
            this.refreshTokenRequest$ = null;
          }),
          shareReplay(1)
        );
      }
      return this.refreshTokenRequest$;
    }
  //#endregion user

  //#region text-blocks
    public getTextBlocks(language: string | null): Observable<Record<string, string>> {
      return this.http.get<Record<string, string>>(
        this.url + '/' + environment.api.version + `/text-blocks${language ? `/${language}` : ''}`,
        {headers: this.headers}
      );
    }
  //#endregion text-blocks

  //#region settings templates
    public getSettingsTemplatesRoot(module?: StorageModule): Observable<StorageFilesystemGet200Response> {
      return this.http.get<StorageFilesystemGet200Response>(
        this.url + '/' + environment.api.version + `/storage/filesystem`,
        {
          headers: this.headers,
          params: module ? {module} : undefined
        }
      );
    }

    public getSettingsTemplatesByDirectory(directory: string, module?: StorageModule): Observable<StorageFilesystemGet200Response> {
      const resolvedDirectory: string = directory && directory.trim().length > 0 ? directory : '';
      return this.http.get<StorageFilesystemGet200Response>(
        this.url + '/' + environment.api.version + `/storage/filesystem${resolvedDirectory ? `/${resolvedDirectory}` : ''}`,
        {
          headers: this.headers,
          params: module ? {module} : undefined
        }
      );
    }

    public removeSettingsTemplate(storageFilesystemId: string | number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/storage/filesystem/${storageFilesystemId}`,
        {headers: this.headers}
      );
    }

    public updateSettingsTemplateFallback(storageFilesystemId: string | number, fallback: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/storage/filesystem/${storageFilesystemId}/fallback`,
        {fallback},
        {headers: this.headers}
      );
    }
  //#endregion settings templates

  //#region settings home links
    public removeSettingsHomeLink(linkId: number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/settings/links/${linkId}`,
        {headers: this.headers}
      );
    }

    public createOrUpdateSettingsHomeLink(data: SettingsDashboardLinkUpdateRequest, existingID: number | null): Observable<DashboardLinkResource> {
      return this.http.put<DashboardLinkResource>(
        this.url + '/' + environment.api.version + `/settings/links${existingID !== null ? `/${existingID}` : ''}`,
        data,
        {headers: this.headers}
      );
    }

    public moveSettingsHomeLink(linkId: number, direction: 'up' | 'down'): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/settings/links/${linkId}/move`,
        {direction},
        {headers: this.headers}
      );
    }
  //#endregion settings home links

  //#region settings profile fields
    public getSettingsProfileFields(): Observable<CustomerProfileSettingsResource> {
      return this.http.get<CustomerProfileSettingsResource>(
        this.url + '/' + environment.api.version + `/settings/profile`,
        {headers: this.headers}
      );
    }

    public updateSettingsProfileFields(data: CustomerProfileSettingsResource): Observable<CustomerProfileSettingsResource> {
      return this.http.put<CustomerProfileSettingsResource>(
        this.url + '/' + environment.api.version + `/settings/profile`,
        data,
        {headers: this.headers}
      );
    }
  //#endregion settings profile fields

  //#region settings dashboard
    public getSettingsDashboard(): Observable<CustomerHomeResource> {
      return this.http.get<CustomerHomeResource>(
        this.url + '/' + environment.api.version + `/settings/home`,
        {headers: this.headers}
      );
    }

    public updateSettingsDashboard(data: CustomerHomeResource): Observable<CustomerHomeResource> {
      return this.http.put<CustomerHomeResource>(
        this.url + '/' + environment.api.version + `/settings/home`,
        data,
        {headers: this.headers}
      );
    }
  //#endregion settings dashboard

  //#region settings user and group management
    //#region user
    public getIAMUserList(state: IAMUserState, search: string, skip: number, limit: number): Observable<IAMUserListResource[]> {
      return this.http.get<IAMUserListResource[]>(
        this.url + '/' + environment.api.version + `/iam/users` +
        `?state=${state}${search !== '' ? `&search=${search}` : ''}&skip=${skip}&limit=${limit}`,
        {headers: this.headers}
      );
    }

    public getIAMUser(user: number): Observable<IAMUserResource> {
      return this.http.get<IAMUserResource>(
        this.url + '/' + environment.api.version + `/iam/user/${user}`,
        {headers: this.headers}
      );
    }

    public getIAMUserGroups(user: number): Observable<IAMUserGroupResource[]> {
      return this.http.get<IAMUserResource>(
        this.url + '/' + environment.api.version + `/iam/user/${user}`,
        {headers: this.headers}
      ).pipe(map((userResource: IAMUserResource) => userResource.groups));
    }
    
    public getIAMUserRights(user: number): Observable<IAMSectionResource[]> {
      return this.http.get<IAMSectionResource[]>(
        this.url + '/' + environment.api.version + `/iam/user/${user}/rights`,
        {headers: this.headers}
      );
    }

    public updateIAMUserRights(user: number, data: IAMRightsUpdateRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/iam/user/${user}/rights`,
        data,
        {headers: this.headers}
      );
    }

    public updateIAMUserActive(data: IAMUserActivateRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/iam/users/activate`,
        data,
        {headers: this.headers}
      );
    }

    public updateIAMUser(userId: number, data: IAMUserUpdateRequest): Observable<UserMinimalResource> {
      return this.http.put<UserMinimalResource>(
        this.url + '/' + environment.api.version + `/iam/user/${userId}`,
        data,
        {headers: this.headers}
      );
    }
    
    public createIAMUser(data: IAMUserUpdateRequest): Observable<UserMinimalResource> {
      return this.http.post<UserMinimalResource>(
        this.url + '/' + environment.api.version + `/iam/user`,
        data,
        {headers: this.headers}
      );
    }
    
    public addIAMGroupToUser(userId: number, groupId: number): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/iam/user/${userId}/group/${groupId}/attach`,
        {},
        {headers: this.headers}
      );
    }

    public removeIAMGroupFromUser(userId: number, groupId: number): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/iam/user/${userId}/group/${groupId}/detach`,
        {},
        {headers: this.headers}
      );
    }

    public deleteIAMUsers(user_ids: number[]): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/iam/users?` + user_ids.map((x) => `user_ids[]=${x}`).join('&'),
        {headers: this.headers}
      );
    }
    //#endregion user
    //#region group
    public getIAMGroupList(search: string, skip: number, limit: number): Observable<IAMGroupListResource[]> {
      return this.http.get<IAMGroupListResource[]>(
        this.url + '/' + environment.api.version + `/iam/groups` +
        `?skip=${skip}&limit=${limit}${search !== '' ? `&search=${search}` : ''}`,
        {headers: this.headers}
      );
    }

    public getIAMGroup(group: number): Observable<IAMGroupResource> {
      return this.http.get<IAMGroupResource>(
        this.url + '/' + environment.api.version + `/iam/group/${group}`,
        {headers: this.headers}
      );
    }

    public getIAMGroupRights(group: number): Observable<IAMSectionResource[]> {
      return this.http.get<IAMSectionResource[]>(
        this.url + '/' + environment.api.version + `/iam/group/${group}/rights`,
        {headers: this.headers}
      );
    }

    public getIAMGroupUsers(group: string | number): Observable<IAMUserInfoResource[]> {
      return this.http.get<IAMGroupResource>(
        this.url + '/' + environment.api.version + `/iam/group/${group}`,
        {headers: this.headers}
      ).pipe(map((groupResource: IAMGroupResource) => groupResource.users));
    }

    public updateIAMGroupRights(group: number, data: IAMRightsUpdateRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/iam/group/${group}/rights`,
        data,
        {headers: this.headers}
      );
    }

    public updateIAMGroup(group: number, data: IAMGroupUpdateRequest): Observable<IAMGroupInfoResource> {
      return this.http.put<IAMGroupInfoResource>(
        this.url + '/' + environment.api.version + `/iam/group/${group}`,
        data,
        {headers: this.headers}
      );
    }

    public createIAMGroup(data: IAMGroupUpdateRequest): Observable<IAMGroupInfoResource> {
      return this.http.post<IAMGroupInfoResource>(
        this.url + '/' + environment.api.version + `/iam/group`,
        data,
        {headers: this.headers}
      );
    }

    public deleteIAMGroups(group_ids: number[]): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/iam/groups?` + group_ids.map((x) => `group_ids[]=${x}`).join('&'),
        {headers: this.headers}
      );
    }
    //#endregion group
  //#endregion settings user and group management

  //#region chat
    public getChatList(filter: ChatListFilterType | null, search: string, limit: number, skip: number): Observable<ChatListResource[]> {
      return this.http.get<ChatListResource[]>(
        this.url + '/' + environment.api.version + 
        `/chats?limit=${limit}&skip=${skip}${search !== '' ? `&search=${search}` : ''}${filter !== null ? `&filter=${filter}` : ''}`,
        {headers: this.headers}
      );
    }

    public findChat(user_ids: number[]): Observable<ChatResource | void> {
      return this.http.get<ChatResource | void>(
        this.url + '/' + environment.api.version + 
        `/chat/find?` + user_ids.map((x) => `user_ids[]=${x}`).join('&'),
        {headers: this.headers}
      );
    }

    public getChatListItem(chatId: number): Observable<ChatResource> {
      return this.http.get<ChatResource>(
        this.url + '/' + environment.api.version + `/chat/${chatId}`,
        {headers: this.headers}
      );
    }

    public getChatListItemShareCounts(chatId: number): Observable<ChatShareCounts> {
      return this.http.get<ChatShareCounts>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/shares`,
        {headers: this.headers}
      );
    }

    public getChatLinkList(chatId: number, after: number, limit: number, search: string): Observable<LinkResource[]> {
      return this.http.get<LinkResource[]>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/links?after=${after}&limit=${limit}${search !== '' ? `&search=${search}` : ''}`,
        {headers: this.headers}
      );
    }

    public getChatFileList(chatId: number, type: 'image' | 'video' | 'audio' | 'file', after: number, limit: number, search: string): Observable<FileStorageResource[]> {
      return this.http.get<FileStorageResource[]>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/files/${type}?after=${after}&limit=${limit}${search !== '' ? `&search=${search}` : ''}`,
        {headers: this.headers}
      );
    }

    public createChat(data: ChatUpdateRequest): Observable<ChatResource> {
      return this.http.post<ChatResource>(
        this.url + '/' + environment.api.version + '/chat',
        data,
        {headers: this.headers}
      );
    }

    public updateChat(chatId: number, data: ChatUpdateRequest): Observable<ChatResource> {
      return this.http.put<ChatResource>(
        this.url + '/' + environment.api.version + '/chat/' + chatId,
        data,
        {headers: this.headers}
      );
    }

    public deleteChat(chatId: number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + '/chat/' + chatId,
        {headers: this.headers}
      );
    }

    public leaveChat(chatId: number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + '/chat/' + chatId + '/leave',
        {headers: this.headers}
      );
    }

    public updateChatNotifications(chatId: number, enabled: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/chat/' + chatId + '/notification',
        {send_push: enabled},
        {headers: this.headers}
      );
    }

    public updateChatIsFavorite(chatId: number, isFavorite: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/chat/' + chatId + '/favorite',
        {favorite: isFavorite},
        {headers: this.headers}
      );
    }

    public updateChatIsBlocked(chatId: number, isBlocked: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + '/chat/' + chatId + '/block',
        {block: isBlocked},
        {headers: this.headers}
      );
    }

    public getChatMessageList(
      chatId: number,
      cursor: number | null,
      direction: ChatMessageCursorType | null,
      limit: number,
      search: string,
      sent_after: string | null = null,
    ) : Observable<HttpResponse<ChatMessageResource[]>> {
      return this.http.get<ChatMessageResource[]>(
        this.url + '/' + environment.api.version + 
        `/chat/${chatId}/messages?limit=${limit}${direction !== null ? `&direction=${direction}` : ''}${cursor !== null ? `&cursor=${cursor}` : ''}${search !== '' ? `&search=${search}` : ''}${sent_after !== null ? `&sent_after=${sent_after}` : ''}`,
        {headers: this.headers, observe: 'response'}
      );
    }

    public getChatMessageReadersList(chatId: number, messageId: number): Observable<ChatMessageReaderResource[]> {
      return this.http.get<ChatMessageReaderResource[]>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/${messageId}/readers`,
        {headers: this.headers}
      );
    }

    public addChatMessage(chatId: number, data: ChatMessageUpdateRequest) : Observable<ChatMessageResource[]> {
      return this.http.post<ChatMessageResource[]>(
        this.url + '/' + environment.api.version + '/chat/' + chatId + '/messages',
        data,
        {headers: this.headers}
      );
    }

    public updateChatMessage(chatId: number, messageId: number, message: string | null) : Observable<ChatMessageResource> {
      return this.http.put<ChatMessageResource>(
        this.url + '/' + environment.api.version + '/chat/' + chatId + '/messages/' + messageId,
        {message},
        {headers: this.headers}
      );
    }

    public setChatMessageFavorite(chatId: number, messageId: number, isFavorite: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/${messageId}/favorite`,
        {favorite: isFavorite},
        {headers: this.headers}
      );
    }

    public setChatMessagePin(chatId: number, messageId: number, isPinned: boolean): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/${messageId}/pin`,
        {pin: isPinned},
        {headers: this.headers}
      );
    }

    public setChatMessageEmoji(chatId: number, messageId: number, emoji: string): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/${messageId}/emoji`,
        {emoji},
        {headers: this.headers}
      );
    }

    public deleteChatMessageForEveryone(chatId: number, messageIds: number[]): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/everyone?` + messageIds.map((x) => `message_ids[]=${x}`).join('&'),
        {headers: this.headers}
      );
    }

    public deleteChatMessageForSelf(chatId: number, messageIds: number[]): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/me?` + messageIds.map((x) => `message_ids[]=${x}`).join('&'),
        {headers: this.headers}
      );
    }

    public reportChatMessage(chatId: number, messageId: number): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/chat/${chatId}/messages/${messageId}/report`,
        {},
        {headers: this.headers}
      );
    }
  //#endregion chat

  //#region dashboard
    public getDashboardHomeLinks(): Observable<DashboardLinkResource[]> {
      return this.http.get<DashboardLinkResource[]>(
        this.url + '/' + environment.api.version + `/settings/links`,
        {headers: this.headers}
      );
    }

    public getDashboardFavoritesList(): Observable<DashboardFavoriteResource[]> {
      return this.http.get<DashboardFavoriteResource[]>(
        this.url + '/' + environment.api.version + `/dashboard/favorites`,
        {headers: this.headers}
      );
    }

    public getDashboardTilesNews(limit: number, skip: number): Observable<DashboardTileResource[]> {
      return this.http.get<DashboardTileResource[]>(
        this.url + '/' + environment.api.version + `/dashboard/tile/news?limit=${limit}&skip=${skip}`,
        {headers: this.headers}
      );
    }

    public getDashboardTilesKnowledgebase(limit: number, skip: number): Observable<DashboardTileResource[]> {
      return this.http.get<DashboardTileResource[]>(
        this.url + '/' + environment.api.version + `/dashboard/tile/knowledgebase?limit=${limit}&skip=${skip}`,
        {headers: this.headers}
      );
    }

    public getDashboardTilesCommunity(limit: number, skip: number): Observable<DashboardTileResource[]> {
      return this.http.get<DashboardTileResource[]>(
        this.url + '/' + environment.api.version + `/dashboard/tile/community?limit=${limit}&skip=${skip}`,
        {headers: this.headers}
      );
    }

    public getDashboardLinks(): Observable<DashboardLinkResource[]> {
      return this.http.get<DashboardLinkResource[]>(
        this.url + '/' + environment.api.version + `/dashboard/links/web`,
        {headers: this.headers}
      );
    }

  //#endregion dashboard

  //#region contacts
    public getContact(userId: number): Observable<UserContactResource> {
      return this.http.get<UserContactResource>(
        this.url + '/' + environment.api.version + '/contact/' + userId,
        {headers: this.headers});
    }

    public getContactList(limit: number, skip: number, field: string, search: string): Observable<UserContactListResource[]> {
      return this.http.get<UserContactListResource[]>(
        this.url + '/' + environment.api.version + '/contacts' +
        `?limit=${limit}&skip=${skip}${field !== '' ? `&field=${field}` : ''}${search !== '' ? `&search=${search}` : ''}`,
        {headers: this.headers}
      );
    }

  //#endregion contacts

  //#region notifications
    public getNotificationsList(
      limit: number,
      after: number, 
      state: NotificationReadState, 
      channels: NotificationChannel[]
    ): Observable<NotificationResource[]>{
      return this.http.get<NotificationResource[]>(
        this.url + '/' + environment.api.version + '/notifications' +
        `?limit=${limit}&after=${after}&state=${state}&` + channels.map((x) => `channels[]=${x}`).join('&'),
        {headers: this.headers}
      );
    }
    
    public setNotificationRead(id: number): Observable<NotificationResource> {
      return this.http.put<NotificationResource>(
        this.url + '/' + environment.api.version + `/notification/${id}/read`,
        {},
        {headers: this.headers}
      );
    }
    
    public setNotificationUnread(id: number): Observable<NotificationResource> {
      return this.http.put<NotificationResource>(
        this.url + '/' + environment.api.version + `/notification/${id}/unread`,
        {},
        {headers: this.headers}
      );
    }

    public setNotificationsReadForChannels(channels: NotificationChannel[]): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/notification/all/read`,
        {
          channels: channels
        },
        {headers: this.headers}
      );
    }

    public setNotificationBadges(): Observable<Map<string, boolean>>{
      return this.http
      .get<Record<string, boolean> | null>(
        this.url + '/' + environment.api.version + `/notification/badges`,
        {headers: this.headers}
      )
      .pipe(
        // HTTP JSON responses are plain objects; normalize to Map for callers that use `.get(...)`.
        map((badges) => new Map<string, boolean>(Object.entries(badges ?? {})))
      );
    }
  //#endregion notifications

  //#region news
    public getNewsPostsList(
      skip?: number | null,
      limit?: number | null,
      search?: string | null,
      onlyFixed?: boolean | null,
      onlyPlanned?: boolean | null,
      onlyExpired?: boolean | null,
      onlyFavorite?: boolean | null,
      creatorId?: number | null,
      groupId?: number | null
    ): Observable<NewsResource[]> {
      const params = [
        skip !== undefined && skip !== null ? `skip=${skip}` : '',
        limit !== undefined && limit !== null ? `limit=${limit}` : '',
        search !== undefined && search !== null && search !== '' ? `search=${encodeURIComponent(search)}` : '',
        onlyFixed !== undefined && onlyFixed !== null ? `only_fixed=${onlyFixed}` : '',
        onlyPlanned !== undefined && onlyPlanned !== null ? `only_planned=${onlyPlanned}` : '',
        onlyExpired !== undefined && onlyExpired !== null ? `only_expired=${onlyExpired}` : '',
        onlyFavorite !== undefined && onlyFavorite !== null ? `only_favorite=${onlyFavorite}` : '',
        creatorId !== undefined && creatorId !== null ? `creator_id=${creatorId}` : '',
        groupId !== undefined && groupId !== null ? `group_id=${groupId}` : ''
      ].filter((entry: string) => entry !== '').join('&');
      const queryString = params !== '' ? `?${params}` : '';

      return this.http.get<NewsResource[]>(
        this.url + '/' + environment.api.version + `/news/posts${queryString}`,
        {headers: this.headers}
      );
    }

    public getNewsPost(
      postId: number
    ): Observable<NewsResource> {
      return this.http.get<NewsResource>(
        this.url + '/' + environment.api.version + `/news/post/${postId}`,
        {headers: this.headers}
      );
    }

    public getNewsPostReaders(
      postId: number,
      skip?: number | null,
      limit?: number | null,
      mode: 'read' | 'unread' | null = null,
      search?: string | null
    ): Observable<NewsUserReadResource[]> {
      const params = [
        skip !== undefined && skip !== null ? `skip=${skip}` : '',
        limit !== undefined && limit !== null ? `limit=${limit}` : '',
        mode !== undefined && mode !== null ? `mode=${mode}` : '',
        search !== undefined && search !== null && search !== '' ? `search=${encodeURIComponent(search)}` : ''
      ].filter((entry: string) => entry !== '').join('&');
      const queryString = params !== '' ? `?${params}` : '';

      return this.http.get<NewsUserReadResource[]>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/readers${queryString}`,
        {headers: this.headers}
      );
    }

    public getNewsPostLikes(postId: number, skip?: number | null, limit?: number | null, search?: string | null): Observable<NewsUserLikeResource[]> {
      const params = [
        skip !== undefined && skip !== null ? `skip=${skip}` : '',
        limit !== undefined && limit !== null ? `limit=${limit}` : '',
        search !== undefined && search !== null && search !== '' ? `search=${encodeURIComponent(search)}` : ''
      ].filter((entry: string) => entry !== '').join('&');
      const queryString = params !== '' ? `?${params}` : '';

      return this.http.get<NewsUserLikeResource[]>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/likes${queryString}`,
        {headers: this.headers}
      );
    }

    public createNewsPost(payload: NewsCreateRequest): Observable<void> {
      return this.http.post<void>(
        this.url + '/' + environment.api.version + '/news/post',
        payload,
        {headers: this.headers}
      );
    }

    public updateNewsPost(postId: number, payload: NewsUpdateRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}`,
        payload,
        {headers: this.headers}
      );
    }

    public deleteNewsPost(postId: number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}`,
        {headers: this.headers}
      );
    }

    public setNewsPostRead(postId: number): Observable<NewsResource | void> {
      return this.http.put<NewsResource | void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/read`,
        {},
        {headers: this.headers}
      );
    }

    public setNewsPostPin(postId: number, payload: NewsPinUpdateRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/pin`,
        payload,
        {headers: this.headers}
      );
    }

    public setNewsPostFavorite(postId: number, favorite: boolean = true): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/favorite`,
        {favorite},
        {headers: this.headers}
      );
    }

    public setNewsPostLike(postId: number, payload: NewsEmojiUpdateRequest): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/like`,
        payload,
        {headers: this.headers}
      );
    }

    public getNewsPostRootComments(postId: number, after?: number | null, limit?: number | null): Observable<NewsCommentResource[]> {
      const params = [
        after !== undefined && after !== null ? `after=${after}` : '',
        limit !== undefined && limit !== null ? `limit=${limit}` : ''
      ].filter((entry: string) => entry !== '').join('&');
      const queryString = params !== '' ? `?${params}` : '';

      return this.http.get<NewsCommentResource[]>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comments${queryString}`,
        {headers: this.headers}
      );
    }

    public getNewsPostCommentReplies(postId: number, commentId: number, after?: number | null, limit?: number | null): Observable<NewsCommentResource[]> {
      const params = [
        after !== undefined && after !== null ? `after=${after}` : '',
        limit !== undefined && limit !== null ? `limit=${limit}` : ''
      ].filter((entry: string) => entry !== '').join('&');
      const queryString = params !== '' ? `?${params}` : '';

      return this.http.get<NewsCommentResource[]>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment/${commentId}/comments${queryString}`,
        {headers: this.headers}
      );
    }

    public getNewsPostCommentLikes(
      postId: number,
      commentId: number,
      skip?: number | null,
      limit?: number | null,
      search?: string | null
    ): Observable<NewsUserLikeResource[]> {
      const params = [
        skip !== undefined && skip !== null ? `skip=${skip}` : '',
        limit !== undefined && limit !== null ? `limit=${limit}` : '',
        search !== undefined && search !== null && search !== '' ? `search=${encodeURIComponent(search)}` : ''
      ].filter((entry: string) => entry !== '').join('&');
      const queryString = params !== '' ? `?${params}` : '';

      return this.http.get<NewsUserLikeResource[]>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment/${commentId}/likes${queryString}`,
        {headers: this.headers}
      );
    }

    public createNewsPostRootComment(postId: number, payload: NewsCommentCreateRequest): Observable<NewsCommentResource> {
      return this.http.post<NewsCommentResource>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment`,
        payload,
        {headers: this.headers}
      );
    }

    public createNewsPostCommentReply(postId: number, commentId: number, payload: NewsCommentCreateRequest): Observable<NewsCommentResource> {
      return this.http.post<NewsCommentResource>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment/${commentId}`,
        payload,
        {headers: this.headers}
      );
    }

    public updateNewsPostComment(postId: number, commentId: number, payload: NewsCommentCreateRequest): Observable<NewsCommentResource> {
      return this.http.put<NewsCommentResource>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment/${commentId}`,
        payload,
        {headers: this.headers}
      );
    }

    public deleteNewsPostComment(postId: number, commentId: number): Observable<void> {
      return this.http.delete<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment/${commentId}`,
        {headers: this.headers}
      );
    }

    public setNewsPostCommentLike(postId: number, commentId: number, like: boolean = true): Observable<void> {
      return this.http.put<void>(
        this.url + '/' + environment.api.version + `/news/post/${postId}/comment/${commentId}/like`,
        {like},
        {headers: this.headers}
      );
    }

    public getNewsReminder(): Observable<string> {
      return this.http.get<string>(
        this.url + '/' + environment.api.version + '/news/reminder',
        {headers: this.headers}
      );
    }
  //#endregion news

  //#region legacy
  public getLegacyToken(): Observable<{token: string}> {
    return this.http.get<{token: string}>(
      this.url + '/' + environment.api.version + '/legacy/token',
      {headers: this.headers}
    )
  }
  //#endregion legacy
}
